import { useEffect, useRef, useState } from "react";
import { TgScreen } from "../../components/TgScreen";
import { Spinner } from "../../components/atoms";
import { generatePhrase } from "../crypto/mnemonic";
import { useWallet } from "../context";
import { haptic } from "../../lib/haptics";

/**
 * Silent wallet creation, Exodus-style.
 *
 * No "write down these 12 words" step. No "verify position 7" challenge.
 * The wallet generates a phrase, encrypts it at rest, and routes the
 * user straight into the app. A persistent "Back up your wallet" banner
 * on the Wallet screen pushes them to reveal-and-acknowledge the phrase
 * later, and that flow lives in src/screens/Backup.tsx.
 *
 * The rationale: the dual-store (TG CloudStorage ciphertext + IndexedDB
 * AES key) recovers automatically across device-swap and browser-clear
 * scenarios. The seed phrase only matters when the TG account itself is
 * lost — rare enough that forcing it as a setup step is friction theatre
 * that just teaches users to screenshot the phrase. Make it easy to back
 * up; don't make it mandatory.
 */
export function CreateSheet({ onDone }: { onBack?: () => void; onDone?: () => void }) {
  const { setup } = useWallet();
  const [error, setError] = useState<string | null>(null);
  // 0 = fresh, 1 = "still working" hint, 2 = retry button visible
  const [stuckLevel, setStuckLevel] = useState<0 | 1 | 2>(0);
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    // Strict-mode guards: this effect runs twice in dev. Without the
    // ref, we'd generate two phrases and race their persistence.
    // Reset on retry — attempt count is in the dep array.
    if (startedRef.current && attempt === 0) return;
    startedRef.current = true;
    setStuckLevel(0);
    setError(null);

    let cancelled = false;
    const hintTimer = setTimeout(() => {
      if (!cancelled) setStuckLevel(1);
    }, 6_000);
    const retryTimer = setTimeout(() => {
      if (!cancelled) setStuckLevel(2);
    }, 15_000);

    void (async () => {
      try {
        const phrase = generatePhrase(12);
        await setup(phrase);
        if (cancelled) return;
        // Wait a beat so the "creating..." state is visible — instant
        // transitions feel like nothing happened.
        await new Promise((r) => setTimeout(r, 350));
        if (cancelled) return;
        haptic("success");
        onDone?.();
      } catch (e) {
        if (!cancelled) {
          haptic("error");
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(hintTimer);
      clearTimeout(retryTimer);
    };
    // setup/onDone are stable enough; attempt drives explicit retries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const retry = () => {
    haptic("medium");
    startedRef.current = false;
    setAttempt((n) => n + 1);
  };

  if (error) {
    return (
      <TgScreen title="Couldn't create wallet">
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "var(--danger)", marginBottom: 10 }}>
            {error}
          </div>
          <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 18 }}>
            Try again. If it keeps happening, your browser might be
            blocking storage in private mode.
          </div>
          <button
            onClick={retry}
            style={{
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 10,
              padding: "10px 22px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            Retry
          </button>
        </div>
      </TgScreen>
    );
  }

  return (
    <TgScreen title="Setting up your wallet">
      <div
        style={{
          padding: "40px 24px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 18,
          minHeight: 500,
          justifyContent: "center",
        }}
      >
        <Spinner size={28} />
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
          Creating your shielded wallet…
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--hint)",
            lineHeight: 1.5,
            maxWidth: 260,
          }}
        >
          {stuckLevel === 0
            ? "Encrypted on your device. We don't see your keys — and you can back up your recovery phrase anytime from the Wallet tab."
            : stuckLevel === 1
              ? "Still working — Telegram CloudStorage can be slow on first write."
              : "This is taking longer than expected. Try again."}
        </div>
        {stuckLevel === 2 && (
          <button
            onClick={retry}
            style={{
              marginTop: 4,
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 10,
              padding: "10px 22px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            Retry
          </button>
        )}
      </div>
    </TgScreen>
  );
}
